import React, { createContext, useCallback, useContext } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { BaseResponse, fetcher } from 'services/httpRequestor';
import { VerifyService } from 'services/verifyService';

// Type cast for JSX usage compatibility
const ReCAPTCHAComponent = ReCAPTCHA as unknown as React.FC<any>;

export const RecaptchaContext = createContext<{
  getRecaptchaToken: () => Promise<string | null>;
  executeVerify: (
    recaptchaToken: string,
  ) => Promise<BaseResponse<{ data: []; error: string }>>;
}>({
  getRecaptchaToken: () => Promise.reject(),
  executeVerify: () => Promise.reject(),
});

function RecaptchaContextProvider({ children }: React.PropsWithChildren<any>) {
  const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();
  const recaptchaSiteKey = ENV.GOOGLE_RECAPTCHA_SITEKEY;

  const getRecaptchaToken = useCallback(() => {
    if (!recaptchaRef.current) {
      return Promise.resolve(null);
    }
    return recaptchaRef.current.executeAsync();
  }, [recaptchaRef]);

  const executeVerify = useCallback(async (recaptchaToken: string) => {
    const verifyService = new VerifyService(fetcher());
    return verifyService.confirmToken(recaptchaToken);
  }, []);

  // Don't render ReCAPTCHA if sitekey is missing
  if (!recaptchaSiteKey) {
    console.warn('GOOGLE_RECAPTCHA_SITEKEY is not configured');
    return children;
  }

  return (
    <RecaptchaContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        getRecaptchaToken,
        executeVerify,
      }}
    >
      {children}
      <ReCAPTCHAComponent
        ref={recaptchaRef}
        size="invisible"
        sitekey={recaptchaSiteKey}
      />
    </RecaptchaContext.Provider>
  );
}

export default RecaptchaContextProvider;

export const useRecaptchaContext = () => useContext(RecaptchaContext);
